import { api } from "./api";

/* ──────────────────────────────────────────────────────────────────────
   Patch E2 — wizard translation helpers
   Convert PropertyWizard's UI shape into API-compatible fields.
   Pure, no side effects, safe to call multiple times.
   ────────────────────────────────────────────────────────────────────── */

// "2 BHK" / "3.5 BHK" → 2 / 3.5 (numeric, for bhkOptions array)
function parseBhk(s) {
  if (typeof s !== "string") return null;
  const m = s.match(/[\d.]+/);
  return m ? Number(m[0]) : null;
}

// "₹1.25 Cr" / "12500000" / 1.25 → 1.25 (in Cr)
// Accepts both raw rupee amounts and Cr values.
function parsePriceToCrore(v) {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return v > 100 ? v / 1e7 : v;
  const s = String(v).replace(/[₹,\s]/g, "").toLowerCase();
  const m = s.match(/[\d.]+/);
  if (!m) return null;
  const n = Number(m[0]);
  if (s.includes("cr")) return n;
  if (s.includes("l")) return n / 100;  // 85L = 0.85 Cr
  return n > 100 ? n / 1e7 : n;          // raw rupees → Cr
}

function formatPriceRange(minCr, maxCr) {
  if (minCr == null && maxCr == null) return "";
  const fmt = (x) => Number(x).toFixed(2).replace(/\.?0+$/, "");
  if (minCr != null && maxCr != null && minCr !== maxCr) {
    return `₹${fmt(minCr)} - ${fmt(maxCr)} Cr`;
  }
  return `₹${fmt(minCr ?? maxCr)} Cr`;
}

/* formatConfigRange — unit-preserving variant of formatBhkRange.
   Takes RAW config strings from the wizard (e.g. "1 RK", "2 BHK",
   "Showroom", "5+ BHK") and returns a display-friendly range.
   Rules:
     - Single config: verbatim ("1 RK" -> "1 RK")
     - Same unit group: collapse ("2 BHK" + "3 BHK" -> "2 & 3 BHK")
     - Mixed units: list ("1 RK" + "2 BHK" -> "1 RK, 2 BHK")
     - Non-numeric configs (Showroom, Studio): appended verbatim
     - Numeric-with-plus preserved ("5+ BHK" -> "5+ BHK", or "3 & 5+ BHK")
   Used for the customer-side card slot so Studio/Commercial/etc. show
   what the admin actually typed rather than getting force-fit into BHK. */
function formatConfigRange(configs) {
  const seen = new Set();
  const unique = [];
  for (const raw of configs) {
    const s = String(raw || "").trim();
    if (!s || seen.has(s)) continue;
    seen.add(s);
    unique.push(s);
  }
  if (unique.length === 0) return "";
  if (unique.length === 1) return unique[0];
  // Group by unit (whatever text comes after the leading number, if any).
  // "1 RK" -> unit "RK", "2 BHK" -> unit "BHK", "Studio" -> unit "".
  const byUnit = new Map();
  const ordered = [];
  for (const s of unique) {
    const m = s.match(/^(\d+(?:\.\d+)?\+?)\s*(.*)$/);
    const rawNum = m ? m[1] : null;
    const unit = m ? (m[2] || "").trim() : "";
    if (!byUnit.has(unit)) { byUnit.set(unit, []); ordered.push(unit); }
    byUnit.get(unit).push({ raw: s, rawNum });
  }
  const parts = ordered.map((unit) => {
    const items = byUnit.get(unit);
    // Only collapse when a unit is present AND every item has a leading number,
    // so "Studio" alone won't try to collapse and multi-word configs like
    // "Studio, Penthouse" list rather than mash together.
    if (unit && items.every((i) => i.rawNum !== null)) {
      const nums = [...new Set(items.map((i) => i.rawNum))]
        .sort((a, b) => parseFloat(a) - parseFloat(b));
      return `${nums.length <= 1 ? nums.join("") : nums.slice(0, -1).join(", ") + " & " + nums[nums.length - 1]} ${unit}`;
    }
    return items.map((i) => i.raw).join(", ");
  });
  return parts.join(", ");
}

function formatBhkRange(bhks) {
  const uniq = [...new Set(bhks.filter((b) => b != null))].sort((a, b) => a - b);
  if (uniq.length === 0) return "";
  if (uniq.length === 1) return `${uniq[0]} BHK`;
  return (uniq.length <= 1 ? uniq.map(String).join("") : uniq.slice(0, -1).map(String).join(", ") + " & " + String(uniq[uniq.length - 1])) + " BHK";
}

function formatAreaRange(areas, unit) {
  const nums = areas.map(Number).filter((n) => !isNaN(n) && n > 0);
  if (nums.length === 0) return "";
  const lo = Math.min(...nums), hi = Math.max(...nums);
  const u = (unit === "sq_m" || unit === "sqm") ? "sqm" : "sqft";
  const fmt = (n) => n.toLocaleString("en-IN");
  return lo === hi ? `${fmt(lo)} ${u}` : `${fmt(lo)} - ${fmt(hi)} ${u}`;
}

// "2027-12-15" → "Dec 2027" (display) + 2027 (year)
function parsePossessionDate(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return { label: "", year: null, status: null };
  const m = dateStr.match(/^(\d{4})-(\d{2})/);
  if (!m) return { label: dateStr, year: null, status: null };
  const yr = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const label = `${months[mo]} ${yr}`;
  // Auto-derive possessionStatus from year vs current year
  const thisYear = new Date().getFullYear();
  let status;
  if (yr <= thisYear)       status = "ready_to_move";
  else if (yr === thisYear + 1) status = "nearing_possession";
  else                      status = "under_construction";
  return { label, year: yr, status };
}

/* Inverse of parsePossessionDate: rebuild a YYYY-MM-DD the <input type="date">
   can display, from the stored possessionLabel ("Dec 2027") + possessionYear.
   possessionDate IS persisted now (properties.possession_date), so on edit we
   reconstruct it here. Uses the same month list as parsePossessionDate so a
   round-trip (reconstruct -> re-save -> parse) yields the same label/year. */
export function possessionDateFromParts(label, year) {
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const yr = year ? Number(year) : null;
  let mo = null;
  if (typeof label === "string" && label) {
    for (let i = 0; i < months.length; i++) {
      if (label.toLowerCase().indexOf(months[i].toLowerCase()) !== -1) { mo = i + 1; break; }
    }
  }
  if (!yr) return "";
  const mm = mo ? String(mo).padStart(2, "0") : "01";
  return `${yr}-${mm}-01`;
}

// PropertyType "Apartment"/"Plot"/"Villa" → api enum "apartment"/"plot"/"villa"
function mapPropertyType(uiType) {
  if (!uiType) return undefined;
  const s = String(uiType).toLowerCase().replace(/\s+/g, "_");
  // API enum values: apartment, villa, plot, duplex, studio, commercial.
  const map = {
    apartment: "apartment",
    villa: "villa",
    plot: "plot",
    duplex: "duplex",
    studio: "studio",
    studio_apartment: "studio",
    commercial: "commercial",
  };
  return map[s] || s;
}


/* Properties API + UI-shape adapter.

   The customer-site `Property` type and the OLD admin's mock seed expect
   certain field names. The DB stores them slightly differently (enums use
   underscores, FKs use IDs). Adapters bridge so UI components don't change. */

// API → UI shape. UI consumers (PropertiesView, advisor-card on site) get
// objects that match the OLD `Property` shape.
export function toUiProperty(r) {
  if (!r) return r;
  return {
    id: r.id,
    slug: r.slug,
    name: r.name,
    builder: r.builder?.name || "",
    builderId: r.builderId,
    builderSlug: r.builder?.slug,
    localitySlug: r.locality?.slug || "",
    localityArea: r.locality?.area || "",
    localityId: r.localityId,
    type: r.type || "apartment",
    status: r.status || "draft",
    possessionStatus: r.possessionStatus,
    possessionYear: r.possessionYear,
    possessionLabel: r.possessionLabel || "",
    /* The real stored date. It used to be reconstructed from the label as
       the 1st of the month, so every reopened property showed the 1st
       whatever day had been chosen -- the day was never persisted. */
    possessionDate: r.possessionDate ? String(r.possessionDate).slice(0, 10) : "",
    bhkRange: r.bhkRange || "",
    bhkOptions: Array.isArray(r.bhkOptions) ? r.bhkOptions : [],
    areaMin: r.areaMin || "",
    areaRange: r.areaRange || "",
    priceDisplay: r.priceDisplay || "",
    priceMin: r.priceMin ?? 0,
    priceMax: r.priceMax ?? 0,
    totalUnits: r.totalUnits ?? 0,
    landParcel: r.landParcel || "",
    towers: r.towers || "",
    floors: r.floors || "",
    villaLength: r.villaLength || "",
    villaWidth: r.villaWidth || "",
    plotLength: r.plotLength || "",
    plotWidth: r.plotWidth || "",
    plotFloorsAllowed: r.plotFloorsAllowed || "",
    listing: r.listing || "",
    expectedRoi: r.expectedRoi || "",
    rera: r.rera || "",
    litigation: r.litigation || "",
    reraPossession: r.reraPossession || "",
    parking: r.parking || "",
    vastu: r.vastu || "",
    naPlot712: r.naPlot712 || "",
    gatedCommunity: r.gatedCommunity || "",
    servantRoom3plus: r.servantRoom3plus || "",
    totalAmenities: r.totalAmenities || "",
    about: r.about || "",
    aboutHeading: r.aboutHeading || "",
    address: r.address || "",
    lat: r.lat ?? "",
    lng: r.lng ?? "",
    mapEmbedUrl: r.mapEmbedUrl || "",
    brochureUrl: r.brochureUrl || "",
    floorPlanPdfUrl: r.floorPlanPdfUrl || "",
    walkthroughUrl: r.walkthroughUrl || "",
    walkthroughLabel: r.walkthroughLabel || "",
    thumbnail: r.thumbnail || "",
    bhkConfigs: Array.isArray(r.bhkConfigs) ? r.bhkConfigs : [],
    amenities: Array.isArray(r.amenities) ? r.amenities : [],
    nearby: Array.isArray(r.nearby) ? r.nearby : [],
    advisor: r.advisor || {},
    specialOffer: (r.specialOffer && Object.keys(r.specialOffer).length)
      ? { enabled: false, badge: "", title: "", discount: "", description: "", validTill: "", youtubeUrl: "", ...r.specialOffer }
      : { enabled: false, badge: "", title: "", discount: "", description: "", validTill: "", youtubeUrl: "" },
    featured: !!r.featured,
    // Property badges + card subtitle. Read-back so opening an edit
    // shows the values the user previously saved (was missing before,
    // which is why the fields "vanished" on re-open).
    reraVerified: !!r.reraVerified,
    zeroBrokerage: !!r.zeroBrokerage,
    shortDescription: r.shortDescription || "",
    sortOrder: r.sortOrder ?? 0,
    isDemo: !!r.isDemo,
    createdAt: r.createdAt ? r.createdAt.slice(0, 10) : "",
    updatedAt: r.updatedAt ? r.updatedAt.slice(0, 10) : "",
    publishedAt: r.publishedAt,
    images: (r.images || []).map((img) => ({
      id: img.id,
      url: img.url,
      altText: img.altText || "",
      category: img.category || "",
      sortOrder: img.sortOrder ?? 0,
    })),
  };
}

// UI → API shape. The PropertyForm's onSave produces a flat object;
// translate to what the API expects.

// E2: convert PropertyWizard's UI shape → API fields. Idempotent.
// Form-path fields (priceDisplay, bhkRange, etc) are preserved if already set.
function translateWizardShape(input) {
  if (!input || typeof input !== "object") return input;
  const out = { ...input };

  // title → name
  if (out.title && !out.name) out.name = out.title;

  // propertyType → type
  if (out.propertyType && !out.type) {
    out.type = mapPropertyType(out.propertyType);
  } else if (out.type) {
    out.type = mapPropertyType(out.type);
  }

  // fullDescription → about (body only). shortDescription is now its
  // OWN field (sent via directKeys) rather than folded into `about` —
  // the card subtitle and the property body are semantically different
  // and were previously getting mashed together, wiping the card
  // subtitle whenever about was empty.
  if (!out.about && out.fullDescription) {
    out.about = out.fullDescription;
  }

  // coverImage → thumbnail
  if (out.coverImage && !out.thumbnail) {
    out.thumbnail = typeof out.coverImage === "string"
      ? out.coverImage
      : (out.coverImage.url || out.coverImage.src || "");
  }

  // reraId → rera
  if (out.reraId && !out.rera) out.rera = out.reraId;

  // possessionDate → possessionLabel + possessionYear + possessionStatus
  if (out.possessionDate) {
    /* The date the wizard shows IS the source of truth for these three.
    
       Each assignment used to be guarded with `&& !out.possessionLabel`,
       which works on create -- the fields are empty, so the conversion fills
       them -- but silently blocks every EDIT: possessionDateFromParts
       rebuilds the date for display, the user changes it, the conversion
       runs, and the result is thrown away because the old value is still
       sitting there. The date appeared to save and never did.
    
       possessionStatus is still derived rather than overwritten when the
       user has set it explicitly: the wizard offers it as its own control
       (BasicInfoStep:63), so an explicit choice must win over one inferred
       from the year. */
    const { label, year, status } = parsePossessionDate(out.possessionDate);
    if (label) out.possessionLabel = label;
    if (year)  out.possessionYear  = year;
    if (status && !out.possessionStatus) out.possessionStatus = status;
  }

  // configs[] → bhkConfigs + bhkRange + areaRange + priceDisplay + bhkOptions
  if (Array.isArray(out.configs) && out.configs.length > 0) {
    const valid = out.configs.filter((c) => c && (c.bhk || c.price || c.carpetArea));

    // bhkRange — unit-preserving. Uses the raw config strings
    // (e.g. "1 RK", "2 BHK", "Showroom") so the card shows what the
    // admin actually typed rather than assuming everything is BHK.
    if (!out.bhkRange) {
      const rawConfigs = valid.map((c) => c.bhk).filter(Boolean);
      const r = formatConfigRange(rawConfigs);
      if (r) out.bhkRange = r;
    }

    // bhkOptions (numeric array)
    if (!out.bhkOptions || out.bhkOptions.length === 0) {
      out.bhkOptions = [...new Set(valid.map((c) => parseBhk(c.bhk)).filter((n) => n != null))];
    }

    // areaRange
    if (!out.areaRange) {
      const areas = valid.map((c) => c.carpetArea);
      const unit = valid[0]?.areaUnit;
      const r = formatAreaRange(areas, unit);
      if (r) out.areaRange = r;
    }

    // areaMin (smallest area)
    if (!out.areaMin) {
      const areas = valid.map((c) => Number(c.carpetArea)).filter((n) => !isNaN(n) && n > 0);
      if (areas.length) out.areaMin = String(Math.min(...areas));
    }

    // priceDisplay
    if (!out.priceDisplay) {
      const prices = valid.map((c) => parsePriceToCrore(c.price)).filter((n) => n != null);
      if (prices.length) out.priceDisplay = formatPriceRange(Math.min(...prices), Math.max(...prices));
    }

    // priceMin / priceMax (in rupees, for sorting/filtering on backend)
    if (!out.priceMin || !out.priceMax) {
      const prices = valid.map((c) => parsePriceToCrore(c.price)).filter((n) => n != null);
      if (prices.length) {
        if (!out.priceMin) out.priceMin = Math.round(Math.min(...prices) * 1e7);
        if (!out.priceMax) out.priceMax = Math.round(Math.max(...prices) * 1e7);
      }
    }

    // bhkConfigs (API shape — preserves the wizard array for detail page)
    if (!out.bhkConfigs || out.bhkConfigs.length === 0) {
      out.bhkConfigs = valid.map((c) => ({
        config: c.bhk || "",
        area: c.carpetArea ? String(c.carpetArea) : undefined,
        builtUp: c.builtUp ? String(c.builtUp) : undefined,
        // Display label wins over the raw number. bhkConfigs stores one
        // field for both: loadFromProperty reads `from` into priceLabel
        // verbatim and into price with non-numerics stripped. Only the
        // numeric was ever written back, so a label like "3 Cr" was captured
        // in the form and silently dropped on save.
        from: (c.priceLabel || "").trim() || (c.price ? String(c.price) : undefined),
        price: c.price ? String(c.price) : undefined,
        units: c.units ? String(c.units) : undefined,
        floorPlan: c.floorPlan || undefined,
        servantRoom: !!c.servantRoom,
      }));
    }
  }

  // amenities — wizard may store as string[]; API expects [{title, desc, icon}]
  if (Array.isArray(out.amenities) && out.amenities.length > 0) {
    out.amenities = out.amenities.map((a) =>
      typeof a === "string" ? { title: a } : a
    );
  }

  // nearby — wizard nests under location.nearby. LocationStep stores
  // objects shaped { id, name, type, distance }; the API expects
  // { place, distance }. Normalize: name (or place) -> place, keep
  // distance, drop everything else. Also accept raw strings.
  if (out.location?.nearby && Array.isArray(out.location.nearby) && !out.nearby) {
    out.nearby = out.location.nearby
      .map((n) => {
        if (typeof n === "string") return { place: n };
        const place = n.place || n.name || "";
        if (!place) return null;
        return n.distance ? { place, distance: n.distance } : { place };
      })
      .filter(Boolean);
  }

  // location.address/lat/lng/mapEmbedUrl -> top-level fields (own columns).
  if (out.location) {
    if (out.location.address && out.address === undefined) out.address = out.location.address;
    if (out.location.lat !== undefined && out.location.lat !== "" && out.lat === undefined) {
      const n = Number(out.location.lat); if (!isNaN(n)) out.lat = n;
    }
    if (out.location.lng !== undefined && out.location.lng !== "" && out.lng === undefined) {
      const n = Number(out.location.lng); if (!isNaN(n)) out.lng = n;
    }
    if (out.location.mapEmbedUrl && out.mapEmbedUrl === undefined) out.mapEmbedUrl = out.location.mapEmbedUrl;
  }

  return out;
}

export function toApiPayload(uiProp) {
  // E2: wizard translation — apply BEFORE direct field copies so existing
  // fields (PropertyForm path) override computed ones.
  uiProp = translateWizardShape(uiProp);

  const p = {};
  const directKeys = [
    "slug", "name", "type", "status", "possessionStatus", "possessionYear",
    "possessionLabel", "possessionDate", "bhkRange", "bhkOptions", "areaMin", "areaRange",
    "priceDisplay", "priceMin", "priceMax", "totalUnits", "landParcel",
    "towers", "floors", "rera", "litigation", "reraPossession", "about",
    "villaLength", "villaWidth", "plotLength", "plotWidth", "plotFloorsAllowed", "listing", "expectedRoi",
    "parking", "vastu", "totalAmenities", "naPlot712", "gatedCommunity", "servantRoom3plus", "aboutHeading",
    "address", "lat", "lng", "mapEmbedUrl",
    "brochureUrl", "floorPlanPdfUrl", "walkthroughUrl", "walkthroughLabel",
    "thumbnail", "bhkConfigs", "amenities", "nearby", "advisor",
    "featured", "sortOrder", "localityId", "builderId",
    "specialOffer",
    // Property badges + card subtitle. Without these in the allowlist
    // the PATCH body would strip them and the API would never see
    // them, so the DB stays empty and the checkboxes "vanish" on edit.
    "reraVerified", "zeroBrokerage", "shortDescription",
  ];
  for (const k of directKeys) {
    if (uiProp[k] === undefined) continue;
    // Drop empty optional strings so backend stores null (cleaner) — EXCEPT
    // for user-clearable fields, where an empty string is a deliberate
    // "remove this value" signal that must reach the backend to null it.
    // (Without this, deleting e.g. a brochure PDF never persisted: the empty
    // string was skipped, so the old value survived and reappeared on edit.)
    const CLEARABLE = [
      "name", "slug",
      "brochureUrl", "floorPlanPdfUrl", "walkthroughUrl", "walkthroughLabel",
      "address", "mapEmbedUrl", "aboutHeading",
      "rera", "litigation", "reraPossession",
      "landParcel", "towers", "floors", "thumbnail",
      "parking", "vastu", "totalAmenities", "naPlot712", "gatedCommunity", "servantRoom3plus",
    ];
    if (typeof uiProp[k] === "string" && uiProp[k] === "" && !CLEARABLE.includes(k)) continue;
    p[k] = uiProp[k];
  }
  return p;
}

export const propertiesApi = {
  async list(filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "" || v === "all") return;
      params.set(k, String(v));
    });
    const qs = params.toString();
    const r = await api.get(`/properties${qs ? `?${qs}` : ""}`);
    return {
      items: (r.items || []).map(toUiProperty),
      total: r.total || 0,
      page: r.page || 1,
      pageSize: r.pageSize || 50,
    };
  },

  async getOne(idOrSlug) {
    return toUiProperty(await api.get(`/properties/${idOrSlug}`));
  },

  async create(uiProp) {
    const r = await api.post("/properties", toApiPayload(uiProp));
    return toUiProperty(r);
  },

  async update(id, patch) {
    const r = await api.patch(`/properties/${id}`, toApiPayload(patch));
    return toUiProperty(r);
  },

  async duplicate(id) {
    return toUiProperty(await api.post(`/properties/${id}/duplicate`, {}));
  },

  async delete(id) {
    return api.delete(`/properties/${id}`);
  },

  async attachImage(propertyId, { url, altText, category, sortOrder }) {
    return api.post(`/properties/${propertyId}/images`, { url, altText, category, sortOrder });
  },

  async detachImage(propertyId, imageId) {
    return api.delete(`/properties/${propertyId}/images/${imageId}`);
  },

  async reorderImages(propertyId, imageIds) {
    return api.post(`/properties/${propertyId}/images/reorder`, { imageIds });
  },
};
